import React from "react";
import "./InstallPage.css";

const InstallPage = () => {
  return (
    <div className="install-page">
      {/* Hero Section */}
      <section className="install-hero">
        <div className="install-hero-content">
          <h1>Welcome to the Beta!</h1>
          <p className="install-subtitle">
            You're just a few steps away from transforming your workouts with
            AI-powered form analysis.
          </p>
        </div>
      </section>

      <section className="app-icon-hero">
        <div className="icon-container">
          <div className="icon-glow"></div>
          <img
            src="/images/app-icon.png"
            alt="Lift Coach AI App Icon"
            className="app-icon"
          />
          <div className="icon-badge">BETA</div>
        </div>
      </section>

      {/* Installation Steps */}
      <section className="installation-steps">
        <div className="steps-container">
          {/* Step 1: Install TestFlight */}
          <div className="install-step glass-container">
            <div className="step-header">
              <div className="step-number">1</div>
              <h2>Install TestFlight</h2>
            </div>

            <div className="step-content">
              <p className="note">
                💡 <strong>Already have TestFlight?</strong> Skip to step 2!
              </p>

              <p className="step-description">
                TestFlight is an official app from Apple for beta testing. You'll need it
                to install and test Lift Coach AI.
              </p>
              <div className="step-actions">
                <a
                  href="https://apps.apple.com/us/app/testflight/id899247664"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-button primary"
                >
                  <span className="button-icon">📱</span>
                  Get TestFlight
                </a>
              </div>
            </div>
          </div>

          {/* Step 2: Install Lift Coach AI */}
          <div className="install-step glass-container">
            <div className="step-header">
              <div className="step-number">2</div>
              <h2>Install Lift Coach AI</h2>
            </div>
            <div className="step-content">
              <p className="step-description">
                Once TestFlight is installed, you can install the Lift Coach AI
                beta.
              </p>

              <p className="step-description">
                You should have received an email from TestFlight inviting you to test the app.
                In that email, click <strong>"View in TestFlight"</strong>. 
                This will bring you to install Lift Coach AI in TestFlight.
              </p>
              <div className="step-details">
                <h4>What happens next:</h4>
                <ol className="install-sequence">
                  <li>TestFlight will open automatically</li>
                  <li>Tap "Accept" to join the beta</li>
                  <li>Tap "Install" to download Lift Coach AI</li>
                  <li>The app will appear on your home screen when ready</li>
                </ol>
                <div className="troubleshooting">
                  <h4>Troubleshooting:</h4>
                  <p>
                    <strong>Can't find your invitation?</strong> Check for an email
                    from TestFlight saying that you have been "invited" to "test Lift Coach AI".
                  </p>
                  <p>
                    <strong>"Unable to Accept Invite"?</strong> If the email address
                    you signed up with isn't the same as your Apple Account, email us
                    to get set up.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3: Begin Testing */}
          <div className="install-step glass-container">
            <div className="step-header">
              <div className="step-number">3</div>
              <h2>Begin Testing & Provide Feedback</h2>
            </div>
            <div className="step-content">
              <p className="step-description">
                Start using Lift Coach AI and help us make it even better with
                your feedback.
              </p>
              <div className="quick-start">
                <div className="start-steps">
                  <div className="start-step">
                    <span className="start-icon">🎯</span>
                    <div>
                      <strong>Grant Camera Access</strong>
                      <p>Required for real-time form analysis</p>
                    </div>
                  </div>
                  <div className="start-step">
                    <span className="start-icon">🏋️</span>
                    <div>
                      <strong>Choose Your Exercise</strong>
                      <p>Start with any supported movement</p>
                    </div>
                  </div>
                  <div className="start-step">
                    <span className="start-icon">📱</span>
                    <div>
                      <strong>Set Up Your Phone</strong>
                      <p>Follow the on-screen positioning guide</p>
                    </div>
                  </div>
                  <div className="start-step">
                    <span className="start-icon">💪</span>
                    <div>
                      <strong>Start Lifting!</strong>
                      <p>Get instant feedback on your form</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="feedback-section">
                <h4>Sending Feedback</h4>
                <div className="feedback-methods">
                  <div className="feedback-method">
                    <strong>Using the TestFlight App</strong>
                    <p>
                      Use the "Send Feedback" button in the TestFlight app. You
                      can optionally include a screenshot of the app.
                    </p>
                  </div>
                  <div className="feedback-method">
                    <strong>Direct Contact</strong>
                    <p>
                      Email us at{" "}
                      <a href="mailto:beta@getliftcoach.com">beta@getliftcoach.com</a>
                      &nbsp;with any questions or concerns.
                    </p>
                  </div>
                </div>

                <div className="feedback-tips">
                  <h4>What We're Looking For:</h4>
                  <ul>
                    <li>Quality on feedback and scoring</li>
                    <li>App stability, performance, and bugs</li>
                    <li>Exercises or features you'd like to see</li>
                  </ul>
                </div>
              </div>

              <div className="troubleshooting">
                <h4>Warnings:</h4>
                <p>
                  As a beta user, <strong>your set videos may be lost</strong>.
                </p>
                <p>
                  We may close testing on{" "}
                  <strong>old versions of the app</strong> at any time. Make
                  sure you are using the latest version of Lift Coach AI.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="support-section">
        <div className="support-container">
          <h2>Thank you!</h2>
          <p>
            We're glad you are testing Lift Coach AI, and we are here to support
            you throughout the beta testing process.
          </p>
          <div className="support-options">
            <a href="mailto:beta@getliftcoach.com" className="support-link">
              <span>📧</span>
              Email Support
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InstallPage;
